import "core-js/es";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";

export class LineChart {
  public static get wireup(): string { return 'scatter-chart'.asDataSelector(); }

  private element: HTMLElement;
  private options: Options;

  constructor(element: HTMLElement, options: Options) {
    if (element == null)
      throw new Error('You must provide an element to ScatterChart');

    this.element = element;
    this.options = options;
    this.createChart()
  }

  public static load(element: HTMLElement, options?: Options): LineChart {
    let chartElement = element as IScatterChartElement;
    if (!chartElement.scatterChart) {
      options = options || <Options>JSON.parse(element.getAttribute('data-scatter-chart'));
      chartElement.scatterChart = new LineChart(element, options);
    }

    return chartElement.scatterChart;
  }

  private createChart(): void {
    let chart = am4core.create(this.element, am4charts.XYChart);

    this.configureChart(chart);
    this.configureXAxis(chart);
    this.configureYAxis(chart);
    this.configureData(chart);
  }

  private configureChart(chart: am4charts.XYChart): void {
    chart.paddingBottom = 0;
    chart.marginBottom = 0;
    chart.legend = new am4charts.Legend();
    chart.legend.markers.template.width = 12;
    chart.legend.markers.template.height = 12;
    chart.legend.fontSize = 12;
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.strokeWidth = 0;
  }

  private configureXAxis(chart: am4charts.XYChart): void {
    let xAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    xAxis.dataFields.category = "category";
    xAxis.fontSize = 12;
  }

  private configureYAxis(chart: am4charts.XYChart): void {
    let yAxis = chart.yAxes.push(new am4charts.ValueAxis());
    yAxis.title.text = this.options.yAxisLabel;
    yAxis.min = 0;
    yAxis.fontSize = 12;
    yAxis.cursorTooltipEnabled = false;
  }

  private configureData(chart: am4charts.XYChart):void {
    let series = this.options.series;

    chart.data = series[0].columns.map(x => { return { category: x.category }; });

    for (let i = 0; i < series.length; i++) {
      this.configureSeries(chart, series[i]);
    }
  }

  private configureSeries(chart: am4charts.XYChart, series: Series): void {
    let lineSeries = chart.series.push(new am4charts.LineSeries());
    lineSeries.data = series.columns;
    lineSeries.dataFields.valueY = "value";
    lineSeries.dataFields.categoryX = "category";
    let color = new am4core.Color(series.color);
    lineSeries.stroke = color;
    lineSeries.fill = color;
    lineSeries.strokeWidth = 2;
    lineSeries.tensionX = this.options.isSmoothed ? .8 : 1;
    lineSeries.name = series.name;
    lineSeries.tooltipText = '{name}: [bold]{valueY} ' + this.options.yAxisUnit;

    let bullet = lineSeries.bullets.push(new am4charts.CircleBullet());
    bullet.fill = color;
    bullet.circle.radius = 3;
  }
}

class Column {
  category: string;
  value: number;
}

class Series {
  name: string;
  color: am4core.iRGB;
  columns: Column[];
}

export interface IScatterChartElement extends HTMLInputElement {
  scatterChart: LineChart;
}

interface Options {
  yAxisLabel: string;
  yAxisUnit: string;
  isSmoothed: boolean;
  series: Series[];
}

export default LineChart;